import { ApiResult } from "@/common/api/ApiResult.model";
import { UseLoading } from "@/common/composition/loading";
import { isNotEmpty } from "@/fp";
import { computed, Ref, ref } from "@vue/composition-api";

interface CollectionApiResponse<T> {
  cursor: string;
  count: number;
  objects: T[];
}

type CollectionDataFn<T> = (
  cursor: string
) => Promise<ApiResult<CollectionApiResponse<T>>>;

type CollectionFnResponse<T> = [
  Ref<T[]>,
  Ref<number>,
  Ref<boolean>,
  () => Promise<void>,
  () => void,
  Readonly<Ref<Readonly<boolean>>>
];

export const UseCollection = <T>(
  load: CollectionDataFn<T>
): CollectionFnResponse<T> => {
  const [is_loading, set_loading] = UseLoading(false).as_arr();
  const entities: Ref<T[]> = ref([]);
  let last_cursor = "";
  const count = ref(0);
  const _has_more_results = ref(true);
  const has_more_results = computed(() => _has_more_results.value);

  const load_entities = async () => {
    set_loading(true);
    const res = await load(last_cursor);
    if (res.success) {
      const { count: _count, cursor, objects } = res.value;
      count.value = _count;
      _has_more_results.value = isNotEmpty(cursor);
      last_cursor = cursor;
      entities.value = [...entities.value, ...objects];
    } else {
      _has_more_results.value = false;
    }
    set_loading(false);
  };

  const clear = () => {
    _has_more_results.value = true;
    entities.value = [];
    count.value = 0;
    last_cursor = "";
  };

  return [entities, count, is_loading, load_entities, clear, has_more_results];
};
